import React, { useState, useEffect } from "react";
import { Box, Button, Snackbar, Alert, alpha, Typography, TextField, InputAdornment } from "@mui/material";
import { Add, TrendingUp, Search, BarChart } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../../services/axiosInstance";

import AdminSideNav from "../../../adminComponents/sidenav";
import AdminHeader from "../../../adminComponents/header";

import RevenueFormModal from "./RevenueFormModal";
import RevenueTable from "./RevenueTable";
import apiEndPointsConfig from "../../../services/apiEndPoints";

const Revenue = () => {
  const navigate = useNavigate();
  const [revenues, setRevenues] = useState([]);
  const [editData, setEditData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const fetchRevenues = async (filters = {}) => {
    try {
      setLoading(true);

      const response = await axiosInstance.get(apiEndPointsConfig.getRevenues, {
        params: {
          userId: filters.userId || undefined,
          year: filters.year || undefined,
          startDate: filters.startDate || undefined,
          endDate: filters.endDate || undefined,
        },
      });

      setRevenues(response.data.data);
    } catch (error) {
      console.error("Revenue fetch error:", error);
      setSnackbar({
        open: true,
        message: "Failed to fetch revenue data",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (formData) => {
    try {
      await axiosInstance.post(apiEndPointsConfig.createOrUpdateRevenue, formData);

      setModalOpen(false);
      setEditData(null);

      setSnackbar({
        open: true,
        message: editData
          ? "Revenue updated successfully!"
          : "Revenue added successfully!",
        severity: "success",
      });

      fetchRevenues();
    } catch (error) {
      console.error("Revenue save error:", error);
      setSnackbar({
        open: true,
        message: "Failed to save revenue data",
        severity: "error",
      });
    }
  };

  const handleEdit = (rev) => {
    setEditData(rev);
    setModalOpen(true);
  };

  const handleAddNew = () => {
    setEditData(null);
    setModalOpen(true);
  };

  useEffect(() => {
    fetchRevenues({ startDate, endDate });
  }, [startDate, endDate]);

  const filteredRevenues = revenues.filter((rev) => {
    const query = searchQuery.toLowerCase();
    const companyName = (rev.user?.companyName || "").toLowerCase();
    const accountManager = (rev.user?.accountManager?.fullName || "").toLowerCase();
    return companyName.includes(query) || accountManager.includes(query);
  });

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ ml: { xs: "0px", md: "200px" } }}>
        <AdminSideNav />

        <Box sx={{ display: { xs: "none", md: "initial" } }}>
          <AdminHeader title="Revenue Management" />
        </Box>

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: { xs: 2, md: 3 },
            bgcolor: (theme) => alpha(theme.palette.grey[50], 0.5),
            minHeight: "100vh",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
              flexWrap: "wrap", // responsive
              gap: 2,
            }}
          >
            {/* Left Section: Title + Count */}
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: (theme) => alpha(theme.palette.success.main, 0.12),
                  mr: 2,
                }}
              >
                <TrendingUp sx={{ color: "success.main" }} />
              </Box>

              <Box>
                <Typography variant="h6" fontWeight={600}>
                  Revenue Records
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {filteredRevenues.length} total record
                  {filteredRevenues.length !== 1 ? "s" : ""}
                </Typography>
              </Box>
            </Box>

            {/* Right Section: Filters and Add Button */}
            <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
              {/* Search by company name */}
              <TextField
                placeholder="Search by company or manager..."
                size="small"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ fontSize: 18, color: "text.disabled" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ width: 220 }}
              />
              <TextField
                type="date"
                label="Start Date"
                size="small"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 150 }}
              />
              <TextField
                type="date"
                label="End Date"
                size="small"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 150 }}
              />
              {(startDate || endDate) && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    setStartDate("");
                    setEndDate("");
                  }}
                  sx={{ textTransform: "none" }}
                >
                  Clear
                </Button>
              )}
              <Button
                variant="outlined"
                color="primary"
                size="large"
                startIcon={<BarChart />}
                onClick={() => navigate("/admin/revenue-analytics")}
                sx={{
                  borderRadius: 2,
                  px: 3,
                  textTransform: "none",
                  fontWeight: 600,
                  transition: "all 0.2s ease",
                }}
              >
                Analytics
              </Button>
              <Button
                variant="contained"
                color="primary"
                size="large"
                startIcon={<Add />}
                onClick={handleAddNew}
                sx={{
                  borderRadius: 2,
                  px: 4,
                  textTransform: "none",
                  fontWeight: 600,
                  boxShadow: 2,
                  "&:hover": {
                    boxShadow: 4,
                    transform: "translateY(-2px)",
                  },
                  transition: "all 0.2s ease",
                }}
              >
                Add Revenue
              </Button>
            </Box>
          </Box>

          <RevenueTable
            revenues={filteredRevenues}
            onEdit={handleEdit}
            loading={loading}
          />
        </Box>
      </Box>

      <RevenueFormModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmit={handleSubmit}
        editData={editData}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default Revenue;
